import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Pencil, X, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const inputCls = "w-full bg-sand border border-ink/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terra/50";
const BLANK = { key: "", label: "", title: "", tagline: "", vehicle: "", order: 1, active: true, days: [{ label: "Day 1", stops: [{ time: "", place: "", note: "" }] }], budget: [{ label: "", amount: 0 }] };

function StopRow({ stop, onChange, onRemove, prefix }) {
  return (
    <div className="grid grid-cols-12 gap-2 items-start" data-testid={`${prefix}-row`}>
      <input value={stop.time} onChange={(e) => onChange({ ...stop, time: e.target.value })} placeholder="7:00 AM" className={`${inputCls} col-span-3`} data-testid={`${prefix}-time`} />
      <input value={stop.place} onChange={(e) => onChange({ ...stop, place: e.target.value })} placeholder="Place" className={`${inputCls} col-span-4`} data-testid={`${prefix}-place`} />
      <input value={stop.note} onChange={(e) => onChange({ ...stop, note: e.target.value })} placeholder="Note" className={`${inputCls} col-span-4`} data-testid={`${prefix}-note`} />
      <button type="button" onClick={onRemove} className="col-span-1 p-2 text-ink/50 hover:text-red-600" data-testid={`${prefix}-remove`}><Trash2 size={14} /></button>
    </div>
  );
}

function PlanForm({ initial, onSaved, onCancel }) {
  const [p, setP] = useState(initial);
  const setDay = (i, day) => setP({ ...p, days: p.days.map((d, j) => (j === i ? day : d)) });
  const total = p.budget.reduce((s, b) => s + (Number(b.amount) || 0), 0);

  const save = async (e) => {
    e.preventDefault();
    if (!p.key.trim() || !p.title.trim() || !p.label.trim()) return toast.error("Key, label and title are required");
    const payload = { ...p, order: Number(p.order) || 0, budget: p.budget.map((b) => ({ label: b.label, amount: Number(b.amount) || 0 })) };
    try {
      if (payload.id) await api.put(`/admin/itineraries/${payload.id}`, payload);
      else await api.post("/admin/itineraries", payload);
      toast.success("Plan saved");
      onSaved();
    } catch {
      toast.error("Save failed");
    }
  };

  return (
    <motion.form initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} onSubmit={save} className="bg-white border border-terra/30 rounded-2xl p-6 mb-6 space-y-5" data-testid="itinerary-form">
      <div className="grid sm:grid-cols-3 gap-3">
        <input value={p.key} onChange={(e) => setP({ ...p, key: e.target.value })} placeholder="Key (e.g. 4-day)" className={inputCls} data-testid="itinerary-field-key" />
        <input value={p.label} onChange={(e) => setP({ ...p, label: e.target.value })} placeholder="Tab label (e.g. 4 Days)" className={inputCls} data-testid="itinerary-field-label" />
        <input type="number" value={p.order} onChange={(e) => setP({ ...p, order: e.target.value })} placeholder="Order" className={inputCls} data-testid="itinerary-field-order" />
        <input value={p.title} onChange={(e) => setP({ ...p, title: e.target.value })} placeholder="Plan title" className={`${inputCls} sm:col-span-3`} data-testid="itinerary-field-title" />
        <input value={p.tagline} onChange={(e) => setP({ ...p, tagline: e.target.value })} placeholder="Tagline" className={`${inputCls} sm:col-span-3`} data-testid="itinerary-field-tagline" />
        <input value={p.vehicle} onChange={(e) => setP({ ...p, vehicle: e.target.value })} placeholder="Suggested vehicle / transport" className={`${inputCls} sm:col-span-3`} data-testid="itinerary-field-vehicle" />
      </div>

      <div className="space-y-4">
        <p className="text-[11px] font-mono uppercase tracking-widest text-ink/60">Days & stops</p>
        {p.days.map((d, i) => (
          <div key={i} className="border border-ink/10 rounded-xl p-4 space-y-2 bg-sand/40" data-testid={`itinerary-day-editor-${i + 1}`}>
            <div className="flex gap-2 items-center">
              <input value={d.label} onChange={(e) => setDay(i, { ...d, label: e.target.value })} placeholder="Day 1 · Puri" className={inputCls} data-testid={`itinerary-day-label-${i + 1}`} />
              <button type="button" onClick={() => setP({ ...p, days: p.days.filter((_, j) => j !== i) })} className="p-2 text-ink/50 hover:text-red-600" data-testid={`itinerary-day-remove-${i + 1}`}><Trash2 size={15} /></button>
            </div>
            {d.stops.map((s, k) => (
              <StopRow key={k} stop={s} prefix={`itinerary-stop-${i + 1}-${k + 1}`}
                onChange={(ns) => setDay(i, { ...d, stops: d.stops.map((x, m) => (m === k ? ns : x)) })}
                onRemove={() => setDay(i, { ...d, stops: d.stops.filter((_, m) => m !== k) })} />
            ))}
            <button type="button" onClick={() => setDay(i, { ...d, stops: [...d.stops, { time: "", place: "", note: "" }] })} className="text-xs font-semibold text-terra flex items-center gap-1" data-testid={`itinerary-add-stop-${i + 1}`}><Plus size={13} /> Add stop</button>
          </div>
        ))}
        <button type="button" onClick={() => setP({ ...p, days: [...p.days, { label: `Day ${p.days.length + 1}`, stops: [{ time: "", place: "", note: "" }] }] })} className="text-sm font-semibold text-ink flex items-center gap-1 border border-ink/15 px-4 py-2 rounded-full hover:bg-sand" data-testid="itinerary-add-day"><Plus size={14} /> Add day</button>
      </div>

      <div className="space-y-2">
        <p className="text-[11px] font-mono uppercase tracking-widest text-ink/60">Budget for two · total ₹{total.toLocaleString("en-IN")}</p>
        {p.budget.map((b, i) => (
          <div key={i} className="grid grid-cols-12 gap-2" data-testid={`itinerary-budget-row-${i + 1}`}>
            <input value={b.label} onChange={(e) => setP({ ...p, budget: p.budget.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} placeholder="Item" className={`${inputCls} col-span-8`} data-testid={`itinerary-budget-label-${i + 1}`} />
            <input type="number" value={b.amount} onChange={(e) => setP({ ...p, budget: p.budget.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)) })} placeholder="₹" className={`${inputCls} col-span-3`} data-testid={`itinerary-budget-amount-${i + 1}`} />
            <button type="button" onClick={() => setP({ ...p, budget: p.budget.filter((_, j) => j !== i) })} className="col-span-1 p-2 text-ink/50 hover:text-red-600" data-testid={`itinerary-budget-remove-${i + 1}`}><Trash2 size={14} /></button>
          </div>
        ))}
        <button type="button" onClick={() => setP({ ...p, budget: [...p.budget, { label: "", amount: 0 }] })} className="text-xs font-semibold text-terra flex items-center gap-1" data-testid="itinerary-add-budget"><Plus size={13} /> Add budget line</button>
      </div>

      <div className="flex items-center gap-3">
        <button type="submit" className="bg-ink text-sand text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-terra transition-colors" data-testid="itinerary-save-button">{p.id ? "Update plan" : "Create plan"}</button>
        <button type="button" onClick={onCancel} className="flex items-center gap-1 text-sm text-ink/60 px-4 py-2.5 rounded-full border border-ink/15 hover:bg-sand" data-testid="itinerary-cancel-button"><X size={14} /> Cancel</button>
        <label className="ml-auto flex items-center gap-2 text-sm"><input type="checkbox" checked={!!p.active} onChange={(e) => setP({ ...p, active: e.target.checked })} className="accent-[#D95D39] w-4 h-4" data-testid="itinerary-field-active" /> Visible on site</label>
      </div>
    </motion.form>
  );
}

export default function ItinerariesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/itineraries").then((r) => setItems(r.data)).catch(() => toast.error("Failed to load plans"));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await api.delete(`/admin/itineraries/${id}`);
    toast.success("Plan deleted");
    load();
  };
  const move = async (item, dir) => {
    const sorted = [...items].sort((a, b) => a.order - b.order);
    const i = sorted.findIndex((x) => x.id === item.id);
    const other = sorted[i + dir];
    if (!other) return;
    await Promise.all([api.put(`/admin/itineraries/${item.id}`, { order: other.order }), api.put(`/admin/itineraries/${other.id}`, { order: item.order })]);
    load();
  };

  return (
    <div data-testid="itineraries-manager">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-serif text-2xl font-bold text-ink">Ready-Made Plans ({items.length})</h2>
        <button onClick={() => setEditing({ ...BLANK, order: items.length + 1 })} className="flex items-center gap-2 bg-terra text-sand text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-ink transition-colors" data-testid="itinerary-add-button"><Plus size={15} /> New plan</button>
      </div>
      {editing && <PlanForm initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
      <div className="grid gap-3">
        {items.map((it) => (
          <div key={it.id} className="bg-white border border-ink/10 rounded-2xl p-4 flex items-center gap-4" data-testid={`itinerary-row-${it.key}`}>
            <div className="flex flex-col">
              <button onClick={() => move(it, -1)} className="p-1 text-ink/40 hover:text-ink" data-testid={`itinerary-up-${it.key}`}><ChevronUp size={14} /></button>
              <button onClick={() => move(it, 1)} className="p-1 text-ink/40 hover:text-ink" data-testid={`itinerary-down-${it.key}`}><ChevronDown size={14} /></button>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-ink truncate">{it.label} · {it.title}</p>
              <p className="text-xs text-ink/50 truncate">{it.days.length} day{it.days.length !== 1 && "s"} · {it.days.reduce((s, d) => s + d.stops.length, 0)} stops · ₹{it.budget.reduce((s, b) => s + Number(b.amount || 0), 0).toLocaleString("en-IN")} · {it.active ? "visible" : "hidden"}</p>
            </div>
            <button onClick={() => setEditing(it)} className="p-2.5 rounded-full text-ink/60 hover:bg-sand hover:text-terra" data-testid={`itinerary-edit-${it.key}`}><Pencil size={16} /></button>
            <button onClick={() => remove(it.id)} className="p-2.5 rounded-full text-ink/60 hover:bg-red-50 hover:text-red-600" data-testid={`itinerary-delete-${it.key}`}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
